import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { captureException } from "@sentry/react";
import { isURL } from "validator";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";

import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import PageListPage from "./PageListPage";
import { AD_PLAN_CUSTOM_PAGE_REDIRECT_LINK } from "constants";
import { PAGES_DOMAIN_URL } from "constants";

export default ({
  id,
  code,
  name,
  list,
  stats,
  views,
  leads,
  redirectLink,
  pageLists,
  refetch,
  onDeleteClick,
  onDuplicateClick,
  listReady,
  listPage,
  editorLoginCode,
}) => {
  const [listFormOpened, setListFormOpened] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listSelectionOpened, setListSelectionOpened] = useState(false);
  const [changeListLoading, setChangeListLoading] = useState(false);
  const [changeError, setChangeError] = useState(null);
  const [linkFormOpened, setLinkFormOpened] = useState(false);
  const [redirectLinkInput, setRedirectLinkInput] = useState(redirectLink);
  const [warnings, setWarnings] = useState([]);
  const [changeLinkLoading, setChangeLinkLoading] = useState(false);
  const [disabledModal, setDisabledModal] = useState(false);
  const [pageName, setPageName] = useState(name);
  const [editingName, setEditingName] = useState(false);
  const [pageNameWarning, setPageNameWarning] = useState(false);

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
    })
  );

  const { profileData } = useSelector(UserProperties);

  // // handle page edit redirect
  // const CREATE_EDITOR_LOGIN_CODE = gql`
  //   mutation CreateEditorLoginCode {
  //     createEditorLoginCode {
  //       resultCode
  //       code
  //     }
  //   }
  // `;

  // const [createEditorLoginCode] = useMutation(CREATE_EDITOR_LOGIN_CODE);

  const onEditClick = async (pageCode) => {
    console.log("Logging into editor", pageCode);

    // const result = await createEditorLoginCode({});

    // console.log("result", result);

    // window
    //   .open(
    //     `${PAGES_DOMAIN_URL}/editor-login?code=${result.data.createEditorLoginCode.code}&page=${pageCode}`,
    //     "_blank"
    //   )
    //   .focus();

    window
      .open(
        `${PAGES_DOMAIN_URL}/editor-login?code=${editorLoginCode}&page=${pageCode}`,
        "_blank"
      )
      .focus();
  };

  const onOpenClick = async (id) => {
    window.open(`${PAGES_DOMAIN_URL}/?code=${id}`, "_blank").focus();
  };

  // handle Maling list settings
  useEffect(() => {
    setSelectedList(JSON.stringify(list));
  }, [list]);

  const changeListToggle = () => {
    setListFormOpened((prevState) => !prevState);
    setLinkFormOpened(false);
  };

  const toggleListSelection = () => {
    setListSelectionOpened((prevState) => !prevState);
  };

  const onListSelect = (list) => {
    setSelectedList(JSON.stringify(list));
    setListSelectionOpened(false);
  };

  const CHANGE_PAGE_LIST = gql`
    mutation ChangePageList($pageId: ID!, $listId: ID!) {
      changePageList(pageId: $pageId, listId: $listId) {
        resultCode
      }
    }
  `;

  const [changePageList] = useMutation(CHANGE_PAGE_LIST);

  const changeList = async () => {
    try {
      console.log("Chaning list", selectedList);

      setWarnings([]);
      setChangeError(null);
      setChangeListLoading(true);

      const result = await changePageList({
        variables: {
          pageId: id,
          listId: JSON.parse(selectedList).id,
        },
      });

      if (result.data.changePageList.resultCode !== 0) {
        setChangeError(
          "An error occurred while processing your request. Please try again or contact support."
        );
        setChangeListLoading(false);
        return;
      }

      refetch();
      setListSelectionOpened(false);
      setTimeout(() => setListFormOpened(false), 500);
      setChangeListLoading(false);
    } catch (error) {
      console.log("changeList error", error);
      setChangeListLoading(false);
      setChangeError(
        "An error occurred while processing your request. Please try again or contact support."
      );
      return;
    }
  };

  // handle Redirect link settings
  const changeLinkToggle = () => {
    if (profileData.adPlan < AD_PLAN_CUSTOM_PAGE_REDIRECT_LINK) {
      setDisabledModal(true);
      return;
    }

    setListFormOpened(false);
    setLinkFormOpened((prevState) => !prevState);
  };

  const handleLinkInputChange = (e) => {
    setRedirectLinkInput(e.target.value);
  };

  const onCloseDisabledModal = () => {
    setDisabledModal(null);
  };

  const CHANGE_PAGE_REDIRECT_LINK = gql`
    mutation ChangePageRedirectLink($pageId: ID!, $link: String!) {
      changePageRedirectLink(pageId: $pageId, link: $link) {
        resultCode
      }
    }
  `;

  const [changePageRedirectLink] = useMutation(CHANGE_PAGE_REDIRECT_LINK);

  const changeLink = async () => {
    try {
      let newWarnings = [];

      if (
        redirectLinkInput.trim().length > 0 &&
        !isURL(redirectLinkInput.trim(), {
          protocols: ["http", "https"],
          require_valid_protocol: true,
          require_protocol: true,
        })
      )
        newWarnings.push("redirectUrl");

      if (newWarnings.length !== 0) {
        setWarnings(newWarnings);
        return;
      }

      setWarnings([]);
      setChangeError(null);
      setChangeLinkLoading(true);

      const result = await changePageRedirectLink({
        variables: {
          pageId: id,
          link: redirectLinkInput,
        },
      });

      if (result.data.changePageRedirectLink.resultCode !== 0) {
        setChangeError(
          "An error occurred while processing your request. Please try again or contact support."
        );
        setChangeLinkLoading(false);
        return;
      }

      refetch();
      setTimeout(() => setLinkFormOpened(false), 500);
      setChangeLinkLoading(false);
    } catch (error) {
      console.log("changeLink error", error);
      setChangeLinkLoading(false);
      setChangeError(
        "An error occurred while processing your request. Please try again or contact support."
      );
    }
  };

  // error handling
  const onCloseErrorModal = () => {
    setChangeError(null);
  };

  // handle Page name change
  const toggleNameEdit = () => {
    setEditingName((prevState) => !prevState);
  };

  const handleNameInputChange = (e) => {
    setPageName(e.target.value);
  };

  const CHANGE_PAGE_NAME = gql`
    mutation ChangePageName($pageId: ID!, $name: String!) {
      changePageName(pageId: $pageId, name: $name) {
        resultCode
      }
    }
  `;

  const [changePageName] = useMutation(CHANGE_PAGE_NAME);

  const updateName = async () => {
    try {
      setEditingName(false);
      setPageNameWarning(false);

      if (pageName.length < 1) {
        setPageNameWarning(true);
        return;
      }

      await changePageName({
        variables: {
          pageId: id,
          name: pageName,
        },
      });
    } catch (error) {
      console.log("changeLink error", error);
      setChangeLinkLoading(false);
      setChangeError(
        "An error occurred while processing your request. Please try again or contact support."
      );
    }
  };

  //console.log("pageLists", pageLists);

  return (
    <>
      <PageListPage
        id={id}
        code={code}
        list={list}
        stats={stats}
        views={views}
        leads={leads}
        redirectLink={redirectLink}
        pageLists={pageLists}
        onEditClick={onEditClick}
        changeListToggle={changeListToggle}
        listFormOpened={listFormOpened}
        selectedList={JSON.parse(selectedList)}
        listSelectionOpened={listSelectionOpened}
        toggleListSelection={toggleListSelection}
        onListSelect={onListSelect}
        changeList={changeList}
        changeListLoading={changeListLoading}
        changeError={changeError}
        onCloseErrorModal={onCloseErrorModal}
        linkFormOpened={linkFormOpened}
        changeLinkToggle={changeLinkToggle}
        redirectLinkInput={redirectLinkInput}
        handleLinkInputChange={handleLinkInputChange}
        warnings={warnings}
        changeLinkLoading={changeLinkLoading}
        changeLink={changeLink}
        disabledModal={disabledModal}
        onCloseDisabledModal={onCloseDisabledModal}
        onOpenClick={onOpenClick}
        onDeleteClick={onDeleteClick}
        onDuplicateClick={onDuplicateClick}
        editingName={editingName}
        toggleNameEdit={toggleNameEdit}
        pageName={pageName}
        handleNameInputChange={handleNameInputChange}
        updateName={updateName}
        pageNameWarning={pageNameWarning}
        listReady={listReady}
        listPage={listPage}
      />
    </>
  );
};
